import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { List, useList } from '@elipssolution/harfang';
import { Avatar, ButtonBase, ListItemIcon, ListItemText, MenuItem, styled, useTheme } from '@mui/material';
import { useCallback, useEffect } from 'react';

import { WidgetComponentProps } from '../../../types/widget';
import { FETCH_CONTACTS, FetchContactsType } from '../../api/contact';
import useCustomRouter from '../../hooks/useCustomRouter';
import { ContactType } from '../../types/contact';

const AVATAR_SIZE = 36;
const LIMIT_CONTACTS_ARRAY_SIZE = 4;
export const CONTACTS_ITEM_HEIGHT = 52;

const Container = styled('div')(({ theme: { palette, shadows, shape } }) => ({
	display: 'flex',
	flexDirection: 'column',
	height: `calc(${CONTACTS_ITEM_HEIGHT}px * 5)`,
	borderRadius: shape.borderRadius * 2,
	border: `1px solid ${palette.divider}`,
	boxShadow: shadows[1],
	overflow: 'hidden',
}));

const ContactRowItem = styled(MenuItem)(({ theme: { spacing } }) => ({
	height: CONTACTS_ITEM_HEIGHT,
	width: '100%',
	paddingLeft: spacing(1),
	paddingRight: spacing(1),
	gap: spacing(1),
}));

const SeeMoreContactsButton = styled(ButtonBase)(({ theme: { palette, typography } }) => ({
	borderTop: `1px solid ${palette.divider}`,
	height: `${CONTACTS_ITEM_HEIGHT - 4}px`,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	fontWeight: 600,
	fontSize: typography.body2.fontSize,
}));

const Picture = styled(Avatar)({
	width: AVATAR_SIZE,
	height: AVATAR_SIZE,
	fontSize: 16,
});

export const ContactRow = ({ id, name, job, hasPicture }: ContactType) => (
	<ContactRowItem>
		<ListItemIcon>
			<Picture alt={name} src={hasPicture ? `/contact/images?contactId=${id}` : ''}>
				{name
					.split(' ')
					.slice(0, 2)
					.map((word) => word.charAt(0))
					.join('')}
			</Picture>
		</ListItemIcon>

		<ListItemText primary={name} secondary={job} />
	</ContactRowItem>
);

const MobileWidgetContact = ({
	widgetWithKey: {
		widget: { name, type },
	},
}: WidgetComponentProps) => {
	const listInstance = useList();

	const { push, query } = useCustomRouter();
	const { shape } = useTheme();

	const listStyle = { flex: 1, borderRadius: shape.borderRadius * 2, border: 'none', boxShadow: 'none' };

	const [fetchContacts, { error: contactsFetchError, networkStatus }] = useLazyQuery<FetchContactsType>(
		FETCH_CONTACTS,
		{
			notifyOnNetworkStatusChange: true,
		},
	);

	const dataSource = useCallback(async () => {
		const { data, error } = await fetchContacts({
			variables: {
				page: {
					limit: LIMIT_CONTACTS_ARRAY_SIZE,
					offset: 0,
				},
			},
		});

		if (error) {
			throw error;
		}

		const {
			contacts: { items = [], count = 0 },
		} = data ?? { contacts: {} };

		return { items, count: Math.min(LIMIT_CONTACTS_ARRAY_SIZE, count) };
	}, [fetchContacts]);

	const handleNavigateToExpandedWidget = useCallback(() => {
		push({ pathname: 'widget', query: { ...query, name, type } }).catch((error) => {
			throw error;
		});
	}, [name, push, query, type]);

	const handleContactClick = useCallback(
		({ id }: { id: ContactType['id'] }) => {
			push({
				pathname: 'widget/contact',
				query: { ...query, name, type, contactId: id },
			}).catch((error) => {
				throw error;
			});
		},
		[name, push, query, type],
	);

	// Reload the list if the query has been refetched
	useEffect(() => {
		networkStatus === NetworkStatus.refetch && listInstance.reload();
	}, [listInstance, networkStatus]);

	return (
		<Container>
			<>
				<List<ContactType>
					list={listInstance}
					style={listStyle}
					dataSource={dataSource}
					rowRenderer={ContactRow}
					onRowClick={handleContactClick}
				/>

				{!contactsFetchError && (
					<SeeMoreContactsButton onClick={handleNavigateToExpandedWidget}>Voir plus</SeeMoreContactsButton>
				)}
			</>
		</Container>
	);
};

export default MobileWidgetContact;
